import React from 'react';
import ShareRewindSlideBase from './ShareRewindSlideBase';
import { RewindStats } from '../../utils/rewindStats';

interface ShareRewindCruiseLineBreakdownSlideProps {
  year: number;
  stats: RewindStats;
}

const ShareRewindCruiseLineBreakdownSlide = React.forwardRef<HTMLDivElement, ShareRewindCruiseLineBreakdownSlideProps>(
  ({ year, stats }, ref) => {
    if (stats.cruiseLineBreakdown.length === 0) {
      return null;
    }

    const maxCount = Math.max(...stats.cruiseLineBreakdown.map(cl => cl.count));
    const displayCount = stats.cruiseLineBreakdown.length;
    
    // Scale based on number of cruise lines
    let textSize = 'text-5xl';
    let barHeight = 'h-12';
    let spacing = 'space-y-8';
    let maxDisplay = 6;
    
    if (displayCount <= 3) {
      textSize = 'text-6xl';
      barHeight = 'h-16';
      spacing = 'space-y-10';
      maxDisplay = 3;
    } else if (displayCount <= 4) {
      textSize = 'text-5xl';
      barHeight = 'h-14';
      spacing = 'space-y-9';
      maxDisplay = 4;
    } else if (displayCount <= 6) {
      textSize = 'text-4xl';
      barHeight = 'h-10';
      spacing = 'space-y-7';
      maxDisplay = 6;
    } else {
      textSize = 'text-3xl';
      barHeight = 'h-8';
      spacing = 'space-y-5';
      maxDisplay = 8;
    }

    return (
      <ShareRewindSlideBase ref={ref} year={year}>
        <div className="flex flex-col h-full">
          {/* Top Section - Title */}
          <div className="flex flex-col items-center justify-center text-center pt-20 pb-8">
            <div className="text-7xl font-bold text-white leading-none mb-4">
              Cruise Line Breakdown
            </div>
          </div>

          {/* Middle Section - Breakdown */}
          <div className="flex-1 flex items-center justify-center px-8">
            <div className={`w-full max-w-5xl ${spacing}`}>
              {stats.cruiseLineBreakdown.slice(0, maxDisplay).map((cruiseLine, index) => {
                const percentage = (cruiseLine.count / maxCount) * 100;
                return (
                  <div key={index} className="space-y-3">
                    <div className="flex justify-between items-center">
                      <div className={`${textSize} font-bold text-white`}>
                        {cruiseLine.cruiseLine}
                      </div>
                      <div className={`${textSize} font-bold text-white`}>
                        {cruiseLine.count} {cruiseLine.count === 1 ? 'Cruise' : 'Cruises'}
                      </div>
                    </div>
                    <div className={`${barHeight} bg-white/20 rounded-full overflow-hidden`}>
                      <div
                        className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {stats.cruiseLineBreakdown.length > maxDisplay && (
                <div className="text-3xl text-white/90 text-center pt-4">
                  +{stats.cruiseLineBreakdown.length - maxDisplay} more cruise lines
                </div>
              )}
            </div>
          </div>

          {/* Bottom Section - Branding */}
          <div className="flex flex-col items-center text-center pb-12">
            <div className="text-4xl font-bold text-white leading-none mb-2">
              Cruising Along Rewind {year}
            </div>
          </div>
        </div>
      </ShareRewindSlideBase>
    );
  }
);

ShareRewindCruiseLineBreakdownSlide.displayName = 'ShareRewindCruiseLineBreakdownSlide';

export default ShareRewindCruiseLineBreakdownSlide;

